import React, { useState, useEffect, useRef } from 'react';
import { FiEdit2 } from 'react-icons/fi';

/**
 * ProjectNameInput component for editing project names
 * Used as the main title in the floorplan viewer
 * @param {Object} props - Component props
 * @param {string} props.initialName - Initial project name
 * @param {Function} props.onNameChange - Function to call when name changes
 * @returns {JSX.Element} ProjectNameInput component
 */
const ProjectNameInput = ({ initialName = 'Untitled Project', onNameChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(initialName || 'Untitled Project');
  const inputRef = useRef(null);
  
  // Update local state when initialName changes
  useEffect(() => {
    if (initialName) {
      setName(initialName);
    }
  }, [initialName]);
  
  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  
  const handleChange = (e) => {
    setName(e.target.value);
  };
  
  const handleBlur = () => {
    finishEditing();
  };
  
  const handleKeyDown = (e) => {
    // Submit on Enter, cancel on Escape
    if (e.key === 'Enter') {
      finishEditing();
    } else if (e.key === 'Escape') {
      setName(initialName || 'Untitled Project');
      setIsEditing(false);
    }
  };
  
  const finishEditing = () => {
    setIsEditing(false);
    const trimmedName = name.trim();
    
    // If name is empty, revert to original or "Untitled Project"
    if (!trimmedName) {
      setName(initialName || 'Untitled Project');
      return;
    }
    
    // Only call onNameChange if the name has actually changed
    if (trimmedName !== initialName) {
      onNameChange(trimmedName);
    }
  };
  
  const startEditing = (e) => {
    e.stopPropagation(); // Prevent triggering other click handlers
    setIsEditing(true);
  };
  
  return (
    <div className="project-name-input" onClick={(e) => e.stopPropagation()}>
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="project-name-field"
          maxLength={50}
        />
      ) : (
        <h2 onClick={startEditing} title="Click to edit project name">
          {name}
          <span className="edit-icon"><FiEdit2 /></span>
        </h2>
      )}
    </div>
  );
};

export default ProjectNameInput; 